import React from "react";
import Modal from "./Modal";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

// [AW Comision]
const APPWHOOP_COMMISSION = 10; // 10% fijo
const IVA_RATE = 19; // 19% IVA en Chile

export default function PriceBreakdownModal({
  isOpen,
  onClose,
  precio = 0, // Precio final con IVA
  precioSinIva = 0,
  utilidadNeta = 0, // Sin IVA
  utilidadNetaConIva = 0, // Con IVA
  comisionAW = 0, // Sin IVA
  comisionAWConIva = 0, // Con IVA
  comisionAmbassador = 0, // Sin IVA
  comisionAmbassadorConIva = 0, // Con IVA
  ambassadorCommission = 0, // Porcentaje de embajadores (0, 3, 5, 10, 15, 20)
  title = "Desglose de Precio",
  subtitle = "Composición detallada del precio del ticket",
}) {
  // Estado para las pestañas (cuando hay embajadores)
  const [activeTab, setActiveTab] = React.useState("with-ambassador");

  // Formatear números a CLP
  const formatCLP = (value) => {
    return new Intl.NumberFormat("es-CL", {
      style: "currency",
      currency: "CLP",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value || 0);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} large="max-w-xl">
      <div className="bg-aw-bgbluedark rounded-2xl p-6 inset-shadow-sm inset-shadow-aw-green shadow-sm shadow-aw-green scrollbar-box thin h-[94dvh] overflow-y-auto">
        {/* Título */}
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-white mb-2 flex items-center gap-3">
            <FontAwesomeIcon
              icon="fa-solid fa-chart-pie"
              className="text-aw-green"
            />
            {title}
          </h2>
          <p className="text-gray-300 text-sm">
            {subtitle}
          </p>
        </div>

        {/* Precio Final Destacado */}
        <div className="bg-gradient-to-r from-aw-green/20 to-aw-green/5 border-2 border-aw-green rounded-xl p-4 mb-6">
          <div className="text-center">
            <div className="text-gray-300 text-sm mb-1">
              Precio Final del Ticket
            </div>
            <div className="text-white font-bold text-4xl">
              {formatCLP(precio)}
            </div>
            <div className="text-aw-green text-xs mt-1">
              (IVA {IVA_RATE}% incluido)
            </div>
          </div>
        </div>

        {/* Tabs - Solo mostrar si hay comisión de embajadores */}
        {ambassadorCommission > 0 && (
          <div className="mb-4">
            <div className="flex gap-2 border-b border-gray-700">
              <button
                onClick={() => setActiveTab("with-ambassador")}
                className={`px-4 py-2 font-semibold transition-all ${
                  activeTab === "with-ambassador"
                    ? "text-aw-green border-b-2 border-aw-green"
                    : "text-gray-400 hover:text-gray-300"
                }`}
              >
                <FontAwesomeIcon icon="fa-solid fa-user-tie" className="mr-2" />
                Con Embajador
              </button>
              <button
                onClick={() => setActiveTab("without-ambassador")}
                className={`px-4 py-2 font-semibold transition-all ${
                  activeTab === "without-ambassador"
                    ? "text-aw-green border-b-2 border-aw-green"
                    : "text-gray-400 hover:text-gray-300"
                }`}
              >
                <FontAwesomeIcon icon="fa-solid fa-store" className="mr-2" />
                Sin Embajador
              </button>
            </div>
          </div>
        )}

        {/* Sección: Valores CON IVA - Con Embajador */}
        {(ambassadorCommission === 0 || activeTab === "with-ambassador") && (
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-aw-green mb-3 flex items-center gap-2">
              <FontAwesomeIcon icon="fa-solid fa-receipt" />
              Desglose con IVA incluido{" "}
              {ambassadorCommission > 0 && "(Venta con Embajador)"}
            </h3>
            <div className="space-y-3">
              {/* 1. Ganancia Neta CON IVA (Prioridad 1) */}
              <div className="bg-aw-bluedark-95 rounded-lg p-4 border border-green-700">
                <div className="flex justify-between items-start mb-2">
                  <div className="flex items-center gap-2">
                    <FontAwesomeIcon
                      icon="fa-solid fa-coins"
                      className="text-green-500"
                    />
                    <span className="text-white font-semibold">
                      Tu Ganancia con IVA
                    </span>
                  </div>
                  <div className="text-white font-bold text-xl">
                    {formatCLP(utilidadNetaConIva)}
                  </div>
                </div>
                <div className="text-gray-400 text-xs">
                  💰 Monto que se te depositará por cada ticket vendido
                  (calculado: {100 - APPWHOOP_COMMISSION - ambassadorCommission}
                  % del precio final)
                </div>
              </div>

              {/* 2. Comisión Embajador CON IVA (Prioridad 2) - Solo si hay embajadores */}
              {ambassadorCommission > 0 && activeTab === "with-ambassador" && (
                <div className="bg-aw-bluedark-95 rounded-lg p-4 border border-gray-700">
                  <div className="flex justify-between items-start mb-2">
                    <div className="flex items-center gap-2">
                      <FontAwesomeIcon
                        icon="fa-solid fa-users"
                        className="text-purple-500"
                      />
                      <span className="text-white font-semibold">
                        Comisión Embajadores con IVA
                      </span>
                    </div>
                    <div className="text-white font-bold text-xl">
                      {formatCLP(comisionAmbassadorConIva)}
                    </div>
                  </div>
                  <div className="text-gray-400 text-xs">
                    Calculado: {ambassadorCommission}% del precio final
                  </div>
                </div>
              )}

              {/* 3. Comisión AppWhoop CON IVA (Prioridad 3 - por resta) */}
              <div className="bg-aw-bluedark-95 rounded-lg p-4 border border-yellow-700">
                <div className="flex justify-between items-start mb-2">
                  <div className="flex items-center gap-2">
                    <FontAwesomeIcon
                      icon="fa-solid fa-building"
                      className="text-yellow-500"
                    />
                    <span className="text-white font-semibold">
                      Comisión AppWhoop con IVA
                    </span>
                  </div>
                  <div className="text-white font-bold text-xl">
                    {formatCLP(comisionAWConIva)}
                  </div>
                </div>
                <div className="text-gray-400 text-xs">
                  Calculado: {APPWHOOP_COMMISSION}% del precio final
                </div>
              </div>
            </div>
          </div>
        )}
        {/* Sección: Valores SIN IVA - Con Embajador */}
        {ambassadorCommission === 0 || activeTab === "with-ambassador" ? (
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-blue-400 mb-3 flex items-center gap-2">
              <FontAwesomeIcon icon="fa-solid fa-file-invoice" />
              Desglose sin IVA (Base imponible){" "}
              {ambassadorCommission > 0 && "(Venta con Embajador)"}
            </h3>
            <div className="space-y-3">
              {/* 1. Precio sin IVA */}
              <div className="bg-aw-bluedark-95 rounded-lg p-4 border border-gray-700">
                <div className="flex justify-between items-start">
                  <div className="flex items-center gap-2">
                    <FontAwesomeIcon
                      icon="fa-solid fa-calculator"
                      className="text-blue-400"
                    />
                    <span className="text-white font-semibold">
                      Precio sin IVA
                    </span>
                  </div>
                  <div className="text-white font-bold text-lg">
                    {formatCLP(precioSinIva)}
                  </div>
                </div>
              </div>

              {/* 2. Ganancia Neta SIN IVA */}
              <div className="bg-aw-bluedark-95 rounded-lg p-4 border border-gray-700">
                <div className="flex justify-between items-start">
                  <div className="flex items-center gap-2">
                    <FontAwesomeIcon
                      icon="fa-solid fa-coins"
                      className="text-green-400"
                    />
                    <span className="text-white font-semibold">
                      Tu Ganancia sin IVA
                    </span>
                  </div>
                  <div className="text-white font-bold text-lg">
                    {formatCLP(utilidadNeta)}
                  </div>
                </div>
              </div>

              {/* 3. Comisión Embajador SIN IVA */}
              {ambassadorCommission > 0 && (
                <div className="bg-aw-bluedark-95 rounded-lg p-4 border border-gray-700">
                  <div className="flex justify-between items-start">
                    <div className="flex items-center gap-2">
                      <FontAwesomeIcon
                        icon="fa-solid fa-users"
                        className="text-purple-400"
                      />
                      <span className="text-white font-semibold">
                        Comisión Embajadores sin IVA
                      </span>
                    </div>
                    <div className="text-white font-bold text-lg">
                      {formatCLP(comisionAmbassador)}
                    </div>
                  </div>
                </div>
              )}

              {/* 4. Comisión AppWhoop SIN IVA (por resta) */}
              <div className="bg-aw-bluedark-95 rounded-lg p-4 border border-gray-700">
                <div className="flex justify-between items-start">
                  <div className="flex items-center gap-2">
                    <FontAwesomeIcon
                      icon="fa-solid fa-building"
                      className="text-yellow-400"
                    />
                    <span className="text-white font-semibold">
                      Comisión AppWhoop sin IVA
                    </span>
                  </div>
                  <div className="text-white font-bold text-lg">
                    {formatCLP(comisionAW)}
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : null}

        {/* Sección: Sin Embajador - Solo si hay comisión definida */}
        {ambassadorCommission > 0 && activeTab === "without-ambassador" && (
          <>
            {/* Sección: Valores CON IVA - Sin Embajador */}
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-aw-green mb-3 flex items-center gap-2">
                <FontAwesomeIcon icon="fa-solid fa-receipt" />
                Desglose con IVA incluido (Venta sin Embajador)
              </h3>
              <div className="space-y-3">
                {/* 1. Ganancia Neta CON IVA - Sin Embajador */}
                <div className="bg-aw-bluedark-95 rounded-lg p-4 border border-green-700">
                  <div className="flex justify-between items-start mb-2">
                    <div className="flex items-center gap-2">
                      <FontAwesomeIcon
                        icon="fa-solid fa-coins"
                        className="text-green-500"
                      />
                      <span className="text-white font-semibold">
                        Tu Ganancia con IVA (Sin Embajador)
                      </span>
                    </div>
                    <div className="text-white font-bold text-xl">
                      {formatCLP(utilidadNetaConIva + comisionAmbassadorConIva)}
                    </div>
                  </div>
                  <div className="text-gray-400 text-xs">
                    💰 Monto que se te depositará por cada ticket vendido sin
                    embajador (calculado: {100 - APPWHOOP_COMMISSION}% del
                    precio final)
                  </div>
                </div>

                {/* 2. Comisión AppWhoop CON IVA */}
                <div className="bg-aw-bluedark-95 rounded-lg p-4 border border-yellow-700">
                  <div className="flex justify-between items-start mb-2">
                    <div className="flex items-center gap-2">
                      <FontAwesomeIcon
                        icon="fa-solid fa-building"
                        className="text-yellow-500"
                      />
                      <span className="text-white font-semibold">
                        Comisión AppWhoop con IVA
                      </span>
                    </div>
                    <div className="text-white font-bold text-xl">
                      {formatCLP(comisionAWConIva)}
                    </div>
                  </div>
                  <div className="text-gray-400 text-xs">
                    Calculado: {APPWHOOP_COMMISSION}% del precio final
                  </div>
                </div>
              </div>
            </div>

            {/* Sección: Valores SIN IVA - Sin Embajador */}
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-blue-400 mb-3 flex items-center gap-2">
                <FontAwesomeIcon icon="fa-solid fa-file-invoice" />
                Desglose sin IVA (Base imponible) (Venta sin Embajador)
              </h3>
              <div className="space-y-3">
                {/* 1. Precio sin IVA */}
                <div className="bg-aw-bluedark-95 rounded-lg p-4 border border-gray-700">
                  <div className="flex justify-between items-start">
                    <div className="flex items-center gap-2">
                      <FontAwesomeIcon
                        icon="fa-solid fa-calculator"
                        className="text-blue-400"
                      />
                      <span className="text-white font-semibold">
                        Precio sin IVA
                      </span>
                    </div>
                    <div className="text-white font-bold text-lg">
                      {formatCLP(precioSinIva)}
                    </div>
                  </div>
                </div>

                {/* 2. Ganancia Neta SIN IVA - Sin Embajador */}
                <div className="bg-aw-bluedark-95 rounded-lg p-4 border border-gray-700">
                  <div className="flex justify-between items-start">
                    <div className="flex items-center gap-2">
                      <FontAwesomeIcon
                        icon="fa-solid fa-coins"
                        className="text-green-400"
                      />
                      <span className="text-white font-semibold">
                        Tu Ganancia sin IVA (Sin Embajador)
                      </span>
                    </div>
                    <div className="text-white font-bold text-lg">
                      {formatCLP(
                        Math.round(
                          utilidadNetaConIva / 1.19 +
                            Math.round(comisionAmbassadorConIva / 1.19),
                        ),
                      )}
                    </div>
                  </div>
                </div>

                {/* 3. Comisión AppWhoop SIN IVA */}
                <div className="bg-aw-bluedark-95 rounded-lg p-4 border border-gray-700">
                  <div className="flex justify-between items-start">
                    <div className="flex items-center gap-2">
                      <FontAwesomeIcon
                        icon="fa-solid fa-building"
                        className="text-yellow-400"
                      />
                      <span className="text-white font-semibold">
                        Comisión AppWhoop sin IVA
                      </span>
                    </div>
                    <div className="text-white font-bold text-lg">
                      {formatCLP(comisionAW)}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}

        {/* Botón de cerrar */}
        <div className="flex justify-end">
          <button
            onClick={onClose}
            className="px-6 py-2 bg-aw-green text-aw-bluedark rounded-lg font-bold hover:scale-95 transition-transform duration-300 flex items-center gap-2"
          >
            <FontAwesomeIcon icon="fa-solid fa-check" />
            Entendido
          </button>
        </div>
      </div>
    </Modal>
  );
}
